import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import Icon from '@mdi/react';
import { mdiClose, mdiArrowLeft, mdiAccountCircleOutline, mdiCogOutline } from '@mdi/js';
import { CURRENCIES, DATE_FORMATS, PER_WEEK_DATE_FORMATS, NUMBER_FORMAT_LOCALES } from 'relevant-shared/constants';
import EffectiveUserSettings from 'relevant-shared/misc/effectiveUserSettings';
import { MiscTasks, changePassword } from '../../api/relevant';
import { stores } from '../../stores';
import { useUserById } from '../../stores/userQuery';
import { IconButton } from '../Button/IconButton';
import { Button } from '../Button/Button';
import { CurrentUserAvatar, Avatar } from '../Avatar';
import { Select } from '../Select/Select';
import { DragDrop } from '../DragDrop';
import { useToast } from '../../hooks/useToast';
import { PasswordInput } from '../TextField/PasswordInput';

const fileToBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = (error) => reject(error);
	});
};

const TabIcons = {
	Profile: mdiAccountCircleOutline,
	Preferences: mdiCogOutline,
};

type Tab = keyof typeof TabIcons;

type ModifiableSettings = {
	dateFormat?: string;
	perWeekDateFormat?: string;
	currency?: string;
	numberFormatLocale?: string;
	picture?: boolean;
};

interface ManageAccountProps {
	onClose: () => void;
}

const FieldBase = [
	{ property: 'dateFormat', title: 'Date format', options: DATE_FORMATS },
	{ property: 'perWeekDateFormat', title: 'Weekly date format', options: PER_WEEK_DATE_FORMATS },
	{ property: 'numberFormatLocale', title: 'Number format', options: NUMBER_FORMAT_LOCALES },
	{
		property: 'currency',
		title: 'Currency',
		options: _.map(CURRENCIES, ({ name, symbol }, value) => ({
			label: name,
			value,
			icon: <span className="text-base">{symbol}</span>,
		})),
	},
];

// TODO Add hover effect to the profile picture?
export function ManageAccount({ onClose }: ManageAccountProps) {
	const userSettings = EffectiveUserSettings.current().getObjectByType('user');
	// @ts-expect-error No ts type for EffectiveUserSettings
	const baseSettings = EffectiveUserSettings.current().aboveLevel('user').all();
	const user = {
		...useUserById(stores.identity.userId()),
		..._.mapValues(EffectiveUserSettings.AvailSettings, () => null),
		...userSettings,
	};
	const [openTab, setOpenTab] = useState<Tab>('Profile');
	const [tabChangeDirection, setTabChangeDirection] = useState<'horizontal' | 'vertical' | null>(null);
	const [changePasswordView, setChangePasswordView] = useState(false);
	const [password, setPassword] = useState('');
	const [passwordError, setPasswordError] = useState<string>(null);
	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');
	const [pendingChanges, setPendingChanges] = useState<ModifiableSettings>({});
	const [fileToUpload, setFileToUpload] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [, showToast] = useToast();

	const defLabel = (s) => `Inherit (${_.find(s.options, { value: baseSettings[s.property] })?.label || 'Unkonwn'})`;
	const fieldSettings = FieldBase.map((s) => ({ ...s, options: [{ label: defLabel(s), value: null }, ...s.options] }));

	// Create preview URLs for uploaded files so we can display them with Avatar before they are uploaded.
	useEffect(() => {
		if (!fileToUpload) return undefined;
		const url = URL.createObjectURL(fileToUpload);
		setPreviewUrl(url);
		return () => {
			URL.revokeObjectURL(url);
		};
	}, [fileToUpload]);

	const resetPasswordFields = () => {
		setPassword('');
		setNewPassword('');
		setConfirmNewPassword('');
	};

	const handleChangeTab = (tab: Tab) => {
		const inPassword = changePasswordView;
		if (changePasswordView) {
			setTabChangeDirection('horizontal');
			resetPasswordFields();
			setChangePasswordView(false);
		}
		if (tab !== openTab) {
			if (!inPassword) setTabChangeDirection('vertical');
			setOpenTab(tab);
		}
	};

	// Restoring something to the original value means it shouldn't be in pendingChanges anymore
	const handleSettingsChange = <K extends keyof ModifiableSettings>(property: K, value: ModifiableSettings[K]) => setPendingChanges((prev) => {
		const originalValue = user[property];
		return originalValue === value ? _.omit(prev, property) : { ...prev, [property]: value };
	});

	const handleResetToInitials = (e: React.MouseEvent) => {
		e.stopPropagation();
		handleSettingsChange('picture', null);
	};

	const handleFileSelect = (file: File) => {
		// Check if file is an image
		if (!file.type.match('image.*')) {
			showToast({
				message: 'Please select an image file',
				status: 'error',
				timeout: 6,
			});
			return;
		}
		// Check file size (optional)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (file.size > maxSize) {
			showToast({
				message: 'File size should not exceed 5MB',
				status: 'error',
				timeout: 6,
			});
			return;
		}
		setPendingChanges((prev) => ({ ...prev, picture: true }));
		setFileToUpload(file);
	};

	const handlePrimaryButtonClick = async () => {
		if (changePasswordView) {
			try {
				const { result } = await changePassword({ password, new: newPassword });
				stores.identity.setAuthToken(result);
				showToast({
					message: 'Your password has been changed.',
					status: 'success',
					timeout: 6,
				});
				resetPasswordFields();
			} catch (error) {
				let message = error.error || 'Failed to change password.';
				if (message === 'Wrong account or password.') {
					message = 'Wrong password.';
				}
				setPasswordError(message);
			}
		} else { // saving other changes
			try {
				const { picture, ...rest } = pendingChanges;
				const pictureModified = 'picture' in pendingChanges;
				let base64Picture: string | null;
				// If picture was edited, determine if it is being reset or another is uploaded
				if (pictureModified) {
					base64Picture = picture ? await fileToBase64(fileToUpload!) : null;
				}
				const editedUser = await MiscTasks.call('changeUserSettings', { ...rest, ...(pictureModified && { picture: base64Picture }) });
				await stores.users.edit(editedUser, true);
				showToast({
					message: 'Your changes have been saved.',
					status: 'success',
					timeout: 6,
				});
				// Merge non-picture changes to user object so they remain visible
				// Picture changes should work automatically
				Object.assign(user, rest);
				setPendingChanges({});
			} catch (error) {
				showToast({
					message: error.message || 'Failed to update user settings.',
					status: 'error',
					timeout: 6,
				});
			}
		}
	};

	const useCurrentUserAvatar = !('picture' in pendingChanges);
	const showPictureResetButton = !!((useCurrentUserAvatar ? user : pendingChanges).picture);

	return (
		<div className="flex flex-col items-start flex-[1_0_0] max-w-186 rounded-lg border border-borders-primary bg-bg-static-surface_default animate-fade-in">
			<div className="flex py-3.5 px-8 justify-between items-center content-center gap-y-2 self-stretch flex-wrap border-b border-borders-primary">
				<span className="font-semibold text-lg">Manage account</span>
				<IconButton icon={<Icon path={mdiClose} />} onClick={onClose} />
			</div>
			<div className="flex items-start flex-[1_0_0] self-stretch">
				<div className="flex px-4 py-6 flex-col items-start gap-3 self-stretch border-r border-borders-primary">
					{Object.entries(TabIcons).map(([tab, icon]: [Tab, string]) => (
						<button
							key={tab}
							className={clsx(
								'flex py-2 px-4 items-center gap-2.5 self-stretch rounded-sm',
								openTab === tab ? 'bg-bg-interactive-light-bg-active_selected' : 'hover:bg-bg-interactive-light-bg-active_hover',
							)}
							onClick={() => handleChangeTab(tab)}
						>
							<Icon path={icon} size="18px" />
							<span className="text-sm">{tab}</span>
						</button>
					))}
				</div>
				<div className="flex flex-col items-start flex-[1_0_0] self-stretch">
					{openTab === 'Profile' && !changePasswordView && (
						<>
							<div className={clsx(
								'flex flex-col items-start flex-[1_0_0] self-stretch px-8 pt-6 gap-8',
								tabChangeDirection === 'vertical' && 'animate-fade-in-up',
								tabChangeDirection === 'horizontal' && 'animate-fade-in-left',
							)}>
								<DragDrop
									onFileSelect={handleFileSelect}
									filetype="image/*"
									className="flex px-6 py-4 justify-center items-center gap-4 self-stretch"
								>
									{useCurrentUserAvatar
										? <CurrentUserAvatar className="size-[80px]" />
										: <Avatar className="size-[80px]" text={user.initials} {...(pendingChanges.picture && { img: previewUrl })} />}
									<div className="flex flex-col items-start gap-2 flex-[1_0_0]">
										<div className="flex pl-4 flex-col items-start gap-1.5 self-stretch">
											<span className="text-sm text-text-secondary">Drag and drop your image or click to browse</span>
											<span className="text-xs text-text-tertiary">Supports images (png, jpg) max size 5MB</span>
										</div>
										{showPictureResetButton && (
											<Button variant="ghost" onClick={handleResetToInitials}>
												Reset to initials
											</Button>
										)}
									</div>
								</DragDrop>
								<div className="flex flex-col items-start gap-4 self-stretch">
									<div className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="font-semibold text-xs">Name</span>
										<input
											type="text"
											placeholder={user.fullname}
											className="flex py-2.5 pl-4 pr-3 items-center self-stretch rounded-sm border border-borders-disabled text-text-disabled text-sm"
											disabled
										/>
									</div>
									<div className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="font-semibold text-xs">E-mail</span>
										<input
											type="text"
											placeholder={user.email}
											className="flex py-2.5 pl-4 pr-3 items-center self-stretch rounded-sm border border-borders-disabled text-text-disabled text-sm"
											disabled
										/>
									</div>
								</div>
							</div>
							<div className="h-38.5 shrink" /> {/* spacer */}
						</>
					)}
					{(openTab === 'Profile' && changePasswordView) && (
						<>
							<div className="flex flex-col items-start flex-[1_0_0] self-stretch pr-8 pt-6 pl-5.75 gap-6 animate-fade-in-right">
								<div className="flex items-center gap-4">
									<IconButton icon={<Icon path={mdiArrowLeft} />} onClick={() => handleChangeTab('Profile')} />
									<span className="font-medium">Reset your password</span>
								</div>
								<div className="flex pl-2.25 pt-4 pb-10 flex-col items-start gap-6 self-stretch">
									<div className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="text-xs font-semibold">Current password</span>
										<PasswordInput
											value={password}
											onChange={(e) => {
												if (passwordError) setPasswordError(null);
												setPassword(e);
											}}
											placeholder="Type your current password"
											error={passwordError}
											stretch
										/>
									</div>
									<div className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="text-xs font-semibold">New password</span>
										<PasswordInput
											value={newPassword}
											onChange={setNewPassword}
											placeholder="Type your new password"
											stretch
										/>
									</div>
									<div className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="text-xs font-semibold">Repeat new password</span>
										<PasswordInput
											value={confirmNewPassword}
											onChange={setConfirmNewPassword}
											placeholder="Repeat your new password"
											error={newPassword !== confirmNewPassword && 'Passwords do not match'}
											stretch
										/>
									</div>
								</div>
							</div>
							<div className="h-22.5 shrink" /> {/* spacer */}
						</>
					)}
					{openTab === 'Preferences' && (
						<div className={clsx(
							'flex flex-col items-start flex-[1_0_0] self-stretch px-8 pt-6',
							tabChangeDirection === 'vertical' && 'animate-fade-in-down',
							tabChangeDirection === 'horizontal' && 'animate-fade-in-left',
						)}>
							<div className="flex pb-4 flex-col items-start gap-2 self-stretch">
								<span className="font-medium">Reporting options</span>
								<span className="text-text-tertiary text-xs">
									The currency setting applies when viewing your dashboard or reports.
									If a report was created with a specific currency setting, that will take precedence.
									Alarms are not affected by this setting.
								</span>
							</div>
							<div className="flex pt-4 pb-10 flex-col items-start gap-6 self-stretch">
								{fieldSettings.map(({ property, title, options }) => (
									<div key={property} className="flex flex-col items-start gap-1.5 self-stretch">
										<span className="text-xs font-semibold">{title}</span>
										{/* TODO Make this an "overwritable option" component? */}
										<Select
											value={property in pendingChanges ? pendingChanges[property] : user[property]}
											options={options}
											onChange={(value) => handleSettingsChange(property, value)}
											fullWidth
										/>
									</div>
								))}
							</div>
						</div>
					)}
					<div className="flex py-4 px-8 justify-end items-center gap-2.5 self-stretch border-t border-borders-primary">
						{openTab === 'Profile' && (
							<Button variant="outlined" onClick={() => setChangePasswordView(!changePasswordView)}>
								{changePasswordView ? 'Cancel' : 'Reset password'}
							</Button>
						)}
						<Button
							variant="filled"
							onClick={handlePrimaryButtonClick}
							disabled={changePasswordView ? newPassword.length === 0 || (newPassword !== confirmNewPassword) : Object.keys(pendingChanges).length === 0}
						>
							{changePasswordView ? 'Reset password' : 'Save changes'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
